import React, { useState, useMemo, useEffect } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import {
    Building2, Users, Shield, CheckSquare, Square,
    UserPlus, Search, Edit, Trash2, UserCheck, UserX,
    FolderOpen, Lock, Save, X, ChevronDown, ChevronRight,
    Clock, AlertCircle, CheckCircle2, RefreshCw
} from 'lucide-react';
import { UserProfile, UserRole, ROLE_PERMISSIONS, PLAN_FEATURES, Client } from '../../types';
import { NAV_LINKS } from '../../constants';
import { getFirestore, collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { logAuditAction } from '../../services/AuditService';

// Default client for display (will be fetched from Firestore in future)
const DEFAULT_CLIENT: Client = {
    clientId: 'autogain-internal',
    name: 'AutoGain Organization',
    plan: 'enterprise',
    enabledApps: PLAN_FEATURES['enterprise'].apps,
    maxUsers: 100,
    maxProjects: 50,
    billingEmail: 'billing@autogain.com',
    subscriptionStatus: 'active',
    createdAt: '2024-01-01T00:00:00Z'
};

const MOCK_CLIENT = DEFAULT_CLIENT;

const MOCK_PROJECTS = [
    { id: 'prj-001', name: 'Aerator Pro Gen 2', memberCount: 12 },
    { id: 'prj-002', name: 'Smart Valve EV', memberCount: 8 },
    { id: 'prj-003', name: 'High-Pressure Pump', memberCount: 15 },
    { id: 'prj-004', name: 'Internal R&D', memberCount: 5 }
];

const ClientAdminDashboard: React.FC = () => {
    const { currentUser, projectList: projects } = useAppContext();
    const [users, setUsers] = useState<UserProfile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['projects', 'apps']));

    // Editable user state
    const [editedUser, setEditedUser] = useState<UserProfile | null>(null);

    // Fetch users from Firestore
    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const db = getFirestore();
            const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(usersQuery);
            const fetchedUsers: UserProfile[] = snapshot.docs.map(docSnap => {
                const data = docSnap.data();
                return {
                    uid: docSnap.id,
                    email: data.email || '',
                    displayName: data.displayName || data.username || 'Unknown',
                    clientId: data.clientId || 'unknown',
                    role: data.role || 'viewer',
                    projectAccess: data.projectAccess || [],
                    appAccess: data.appAccess || [],
                    itemAccess: data.itemAccess || { canCreate: false, canEdit: false, canDelete: false, canApprove: false, canExport: false },
                    status: data.isApproved ? 'active' : (data.role === 'pending' ? 'invited' : 'suspended'),
                    createdAt: data.createdAt || new Date().toISOString(),
                    createdBy: data.createdBy || 'system',
                    lastLoginAt: data.lastLoginAt,
                    department: data.department,
                    isApproved: data.isApproved
                } as UserProfile;
            });
            setUsers(fetchedUsers);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Get all apps from NAV_LINKS
    const allApps = useMemo(() => {
        return NAV_LINKS.reduce((acc, link) => {
            if (link.children) {
                acc.push(...link.children.map(c => ({
                    path: c.path,
                    name: c.name,
                    icon: c.icon,
                    category: link.name
                })));
            } else {
                acc.push({ path: link.path, name: link.name, icon: link.icon, category: 'Main' });
            }
            return acc;
        }, [] as { path: string; name: string; icon: React.ReactNode; category: string }[]);
    }, []);

    // Filter users
    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const matchesSearch = user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesRole = roleFilter === 'all' || user.role === roleFilter;
            return matchesSearch && matchesRole;
        });
    }, [users, searchTerm, roleFilter]);

    const toggleSection = (section: string) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(section)) {
                next.delete(section);
            } else {
                next.add(section);
            }
            return next;
        });
    };

    const handleSelectUser = (user: UserProfile) => {
        setSelectedUser(user);
        setEditedUser({ ...user });
        setEditMode(false);
    };

    // SAVE CHANGES - Update Firestore
    const handleSaveChanges = async () => {
        if (!editedUser) return;
        try {
            const db = getFirestore();
            await updateDoc(doc(db, 'users', editedUser.uid), {
                role: editedUser.role,
                projectAccess: editedUser.projectAccess,
                appAccess: editedUser.appAccess,
                itemAccess: editedUser.itemAccess,
                isApproved: editedUser.status === 'active',
                updatedAt: new Date().toISOString()
            });

            // Audit log
            await logAuditAction({
                actorId: (currentUser as any)?.uid || 'unknown',
                actorEmail: currentUser?.email || 'admin',
                action: 'UPDATE_USER_PERMISSIONS',
                targetUserId: editedUser.uid,
                targetUserEmail: editedUser.email,
                details: `Updated permissions for ${editedUser.displayName}`
            });

            setSelectedUser(editedUser);
            setEditMode(false);
            fetchUsers(); // Refresh
        } catch (error) {
            console.error('Error saving changes:', error);
            alert('Failed to save changes. Check console for details.');
        }
    };

    // APPROVE USER
    const handleApproveUser = async (user: UserProfile) => {
        try {
            const db = getFirestore();
            await updateDoc(doc(db, 'users', user.uid), {
                isApproved: true,
                role: (user.role as string) === 'pending' ? 'engineer' : user.role,
                updatedAt: new Date().toISOString()
            });

            await logAuditAction({
                actorId: (currentUser as any)?.uid || 'unknown',
                actorEmail: currentUser?.email || 'admin',
                action: 'APPROVE_USER',
                targetUserId: user.uid,
                targetUserEmail: user.email,
                details: `Approved access for ${user.displayName}`
            });

            fetchUsers();
        } catch (error) {
            console.error('Error approving user:', error);
            alert('Failed to approve user.');
        }
    };

    // SUSPEND USER
    const handleSuspendUser = async (user: UserProfile) => {
        try {
            const db = getFirestore();
            await updateDoc(doc(db, 'users', user.uid), {
                isApproved: false,
                updatedAt: new Date().toISOString()
            });

            await logAuditAction({
                actorId: (currentUser as any)?.uid || 'unknown',
                actorEmail: currentUser?.email || 'admin',
                action: 'SUSPEND_USER',
                targetUserId: user.uid,
                targetUserEmail: user.email,
                details: `Suspended access for ${user.displayName}`
            });

            fetchUsers();
        } catch (error) {
            console.error('Error suspending user:', error);
            alert('Failed to suspend user.');
        }
    };

    // DELETE USER
    const handleDeleteUser = async () => {
        if (!selectedUser) return;
        try {
            const db = getFirestore();
            await deleteDoc(doc(db, 'users', selectedUser.uid));

            await logAuditAction({
                actorId: (currentUser as any)?.uid || 'unknown',
                actorEmail: currentUser?.email || 'admin',
                action: 'DELETE_USER',
                targetUserId: selectedUser.uid,
                targetUserEmail: selectedUser.email,
                details: `Deleted user ${selectedUser.displayName}`
            });

            setShowDeleteModal(false);
            setSelectedUser(null);
            setEditedUser(null);
            fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('Failed to delete user. Ensure you have permission.');
        }
    };

    const toggleProjectAccess = (projectId: string) => {
        if (!editedUser) return;
        const newAccess = editedUser.projectAccess.includes(projectId)
            ? editedUser.projectAccess.filter(p => p !== projectId)
            : [...editedUser.projectAccess, projectId];
        setEditedUser({ ...editedUser, projectAccess: newAccess });
    };

    const toggleAppAccess = (appPath: string) => {
        if (!editedUser) return;
        const newAccess = editedUser.appAccess.includes(appPath)
            ? editedUser.appAccess.filter(a => a !== appPath)
            : [...editedUser.appAccess, appPath];
        setEditedUser({ ...editedUser, appAccess: newAccess });
    };

    const getStatusBadge = (status: UserProfile['status']) => {
        switch (status) {
            case 'active':
                return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Active</span>;
            case 'invited':
                return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">Pending</span>;
            case 'suspended':
                return <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Suspended</span>;
        }
    };

    const getRoleBadge = (role: UserRole) => {
        const colors: Record<UserRole, string> = {
            autoGainAdmin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
            clientAdmin: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
            projectManager: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
            engineer: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
            viewer: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
        };
        const permission = ROLE_PERMISSIONS[role];
        if (!permission) return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Unknown Role</span>;
        return <span className={`px-2 py-0.5 text-xs rounded-full ${colors[role] || 'bg-gray-100'}`}>{permission.description}</span>;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
                        <Building2 className="text-primary" />
                        AutoGain Admin - Client Management
                    </h1>
                    <p className="text-text-secondary mt-1">
                        Manage client companies, subscriptions, and enabled apps (AutoGain staff only)
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => fetchUsers()}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-3 py-2 text-text-secondary hover:bg-background rounded-lg transition-colors"
                    >
                        <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                    <div className="text-right text-sm">
                        <div className="text-text-secondary">Plan: <span className="font-semibold text-primary capitalize">{DEFAULT_CLIENT.plan}</span></div>
                        <div className="text-text-secondary">Users: <span className="font-semibold">{users.length}/{DEFAULT_CLIENT.maxUsers}</span></div>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                    >
                        <UserPlus size={18} />
                        Add User
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <Users className="text-blue-600" size={20} />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">{users.filter(u => u.status === 'active').length}</div>
                            <div className="text-sm text-text-secondary">Active Users</div>
                        </div>
                    </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                            <Clock className="text-yellow-600" size={20} />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">{users.filter(u => u.status === 'invited').length}</div>
                            <div className="text-sm text-text-secondary">Pending Invites</div>
                        </div>
                    </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                            <FolderOpen className="text-green-600" size={20} />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">{projects?.length || 0}</div>
                            <div className="text-sm text-text-secondary">Projects</div>
                        </div>
                    </div>
                </div>
                <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                            <Shield className="text-purple-600" size={20} />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">{users.filter(u => u.role === 'clientAdmin' || u.role === 'autoGainAdmin').length}</div>
                            <div className="text-sm text-text-secondary">Administrators</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* User List */}
                <div className="lg:col-span-1 bg-card border border-border rounded-lg shadow-sm">
                    <div className="p-4 border-b border-border">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={16} />
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none"
                                />
                            </div>
                            <select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value as UserRole | 'all')}
                                className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
                            >
                                <option value="all">All Roles</option>
                                <option value="clientAdmin">Admin</option>
                                <option value="projectManager">Manager</option>
                                <option value="engineer">Engineer</option>
                                <option value="viewer">Viewer</option>
                            </select>
                        </div>
                        <div className="text-sm text-text-secondary">
                            {filteredUsers.length} users
                        </div>
                    </div>
                    <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
                        {filteredUsers.map(user => (
                            <div
                                key={user.uid}
                                onClick={() => handleSelectUser(user)}
                                className={`p-4 cursor-pointer hover:bg-background/50 transition-colors ${selectedUser?.uid === user.uid ? 'bg-primary/5 border-l-4 border-l-primary' : ''
                                    }`}
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="font-medium">{user.displayName}</div>
                                        <div className="text-sm text-text-secondary">{user.email}</div>
                                        <div className="flex items-center gap-2 mt-1">
                                            {getRoleBadge(user.role)}
                                            {getStatusBadge(user.status)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* User Details */}
                <div className="lg:col-span-2 bg-card border border-border rounded-lg shadow-sm">
                    {selectedUser && editedUser ? (
                        <>
                            <div className="p-4 border-b border-border flex justify-between items-center">
                                <div>
                                    <h2 className="text-lg font-semibold">{selectedUser.displayName}</h2>
                                    <div className="text-sm text-text-secondary">{selectedUser.email}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {editMode ? (
                                        <>
                                            <button
                                                onClick={() => {
                                                    setEditedUser({ ...selectedUser });
                                                    setEditMode(false);
                                                }}
                                                className="px-3 py-2 text-text-secondary hover:bg-background rounded-lg flex items-center gap-2"
                                            >
                                                <X size={16} />
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleSaveChanges}
                                                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
                                            >
                                                <Save size={16} />
                                                Save Changes
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {/* Approve/Suspend buttons */}
                                            {selectedUser.status !== 'active' ? (
                                                <button
                                                    onClick={() => handleApproveUser(selectedUser)}
                                                    className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                                                >
                                                    <UserCheck size={16} />
                                                    Approve
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleSuspendUser(selectedUser)}
                                                    className="px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                                                >
                                                    <UserX size={16} />
                                                    Suspend
                                                </button>
                                            )}
                                            {/* Edit button */}
                                            <button
                                                onClick={() => setEditMode(true)}
                                                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
                                            >
                                                <Edit size={16} />
                                                Edit
                                            </button>
                                            {/* Delete button */}
                                            <button
                                                onClick={() => setShowDeleteModal(true)}
                                                className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                                            >
                                                <Trash2 size={16} />
                                                Delete
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="p-4 space-y-6">
                                {/* Role Selection */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Role</label>
                                    <select
                                        value={editedUser.role}
                                        onChange={(e) => setEditedUser({ ...editedUser, role: e.target.value as UserRole })}
                                        disabled={!editMode}
                                        className="w-full px-3 py-2 bg-background border border-border rounded-lg disabled:opacity-50"
                                    >
                                        <option value="clientAdmin">Organization Administrator</option>
                                        <option value="projectManager">Project Manager</option>
                                        <option value="engineer">Engineering User</option>
                                        <option value="viewer">Read-Only Viewer</option>
                                    </select>
                                    <p className="text-xs text-text-secondary mt-1">
                                        {ROLE_PERMISSIONS[editedUser.role]?.description || 'Unknown Role'}
                                    </p>
                                </div>

                                {/* Project Access */}
                                <div className="border border-border rounded-lg overflow-hidden">
                                    <div
                                        className="p-3 bg-background/50 flex items-center justify-between cursor-pointer"
                                        onClick={() => toggleSection('projects')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <FolderOpen size={18} className="text-primary" />
                                            <span className="font-medium">Project Access</span>
                                            <span className="text-sm text-text-secondary">
                                                ({editedUser.projectAccess.length === 0 ? 'None' :
                                                    editedUser.projectAccess.includes('*') ? 'All' :
                                                        editedUser.projectAccess.length})
                                            </span>
                                        </div>
                                        {expandedSections.has('projects') ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                    </div>
                                    {expandedSections.has('projects') && (
                                        <div className="p-4 space-y-2">
                                            {MOCK_PROJECTS.map(project => (
                                                <div
                                                    key={project.id}
                                                    onClick={() => editMode && toggleProjectAccess(project.id)}
                                                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${editMode ? 'cursor-pointer hover:bg-background/50' : ''
                                                        } ${editedUser.projectAccess.includes(project.id) || editedUser.projectAccess.includes('*')
                                                            ? 'bg-primary/5 border-primary/20'
                                                            : 'border-border'
                                                        }`}
                                                >
                                                    {editedUser.projectAccess.includes(project.id) || editedUser.projectAccess.includes('*') ? (
                                                        <CheckSquare className="text-primary" size={18} />
                                                    ) : (
                                                        <Square className="text-text-secondary/50" size={18} />
                                                    )}
                                                    <div className="flex-1">
                                                        <div className="font-medium">{project.name}</div>
                                                        <div className="text-xs text-text-secondary">{project.memberCount} members</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* App Access */}
                                <div className="border border-border rounded-lg overflow-hidden">
                                    <div
                                        className="p-3 bg-background/50 flex items-center justify-between cursor-pointer"
                                        onClick={() => toggleSection('apps')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Shield size={18} className="text-primary" />
                                            <span className="font-medium">App Access</span>
                                            <span className="text-sm text-text-secondary">
                                                ({editedUser.appAccess.length === 0 ? 'None' :
                                                    editedUser.appAccess.includes('*') ? 'All' :
                                                        editedUser.appAccess.length})
                                            </span>
                                        </div>
                                        {expandedSections.has('apps') ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                    </div>
                                    {expandedSections.has('apps') && (
                                        <div className="p-4">
                                            <div className="grid grid-cols-2 gap-2">
                                                {allApps.filter(app =>
                                                    MOCK_CLIENT.enabledApps.includes(app.path) ||
                                                    MOCK_CLIENT.enabledApps.includes('*')
                                                ).map(app => (
                                                    <div
                                                        key={app.path}
                                                        onClick={() => editMode && toggleAppAccess(app.path)}
                                                        className={`flex items-center gap-2 p-2 rounded-lg border text-sm transition-colors ${editMode ? 'cursor-pointer hover:bg-background/50' : ''
                                                            } ${editedUser.appAccess.includes(app.path) || editedUser.appAccess.includes('*')
                                                                ? 'bg-primary/5 border-primary/20'
                                                                : 'border-border'
                                                            }`}
                                                    >
                                                        {editedUser.appAccess.includes(app.path) || editedUser.appAccess.includes('*') ? (
                                                            <CheckSquare className="text-primary flex-shrink-0" size={16} />
                                                        ) : (
                                                            <Square className="text-text-secondary/50 flex-shrink-0" size={16} />
                                                        )}
                                                        <span className="truncate">{app.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Item Permissions */}
                                <div className="border border-border rounded-lg overflow-hidden">
                                    <div
                                        className="p-3 bg-background/50 flex items-center justify-between cursor-pointer"
                                        onClick={() => toggleSection('items')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Lock size={18} className="text-primary" />
                                            <span className="font-medium">Item Permissions</span>
                                        </div>
                                        {expandedSections.has('items') ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                    </div>
                                    {expandedSections.has('items') && (
                                        <div className="p-4 grid grid-cols-2 gap-3">
                                            {Object.entries(editedUser.itemAccess).map(([key, value]) => (
                                                <div
                                                    key={key}
                                                    onClick={() => {
                                                        if (editMode) {
                                                            setEditedUser({
                                                                ...editedUser,
                                                                itemAccess: {
                                                                    ...editedUser.itemAccess,
                                                                    [key]: !value
                                                                }
                                                            });
                                                        }
                                                    }}
                                                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${editMode ? 'cursor-pointer hover:bg-background/50' : ''
                                                        } ${value ? 'bg-green-50 dark:bg-green-900/20 border-green-200' : 'border-border'}`}
                                                >
                                                    {value ? (
                                                        <CheckCircle2 className="text-green-600" size={18} />
                                                    ) : (
                                                        <AlertCircle className="text-text-secondary/50" size={18} />
                                                    )}
                                                    <span className="capitalize">{key.replace('can', 'Can ')}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* User Info */}
                                <div className="text-sm text-text-secondary space-y-1 pt-4 border-t border-border">
                                    <div>Created: {new Date(editedUser.createdAt).toLocaleDateString()}</div>
                                    {editedUser.lastLoginAt && (
                                        <div>Last Login: {new Date(editedUser.lastLoginAt).toLocaleString()}</div>
                                    )}
                                    {editedUser.department && <div>Department: {editedUser.department}</div>}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="p-12 text-center text-text-secondary">
                            <Users className="mx-auto mb-4 text-text-secondary/30" size={48} />
                            <p className="text-lg">Select a user to manage their permissions</p>
                            <p className="text-sm mt-2">You can assign projects, enable apps, and set item-level permissions</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Create User Modal - Direct creation without email */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card p-6 rounded-lg shadow-lg w-full max-w-md border border-border">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold flex items-center gap-2">
                                <UserPlus size={20} className="text-primary" />
                                Create New User
                            </h3>
                            <button onClick={() => setShowCreateModal(false)} className="text-text-secondary hover:text-text-primary">
                                <X size={20} />
                            </button>
                        </div>
                        <form className="space-y-4" onSubmit={(e) => {
                            e.preventDefault();
                            // In production, this would call the backend API
                            console.log('User created successfully!');
                            setShowCreateModal(false);
                        }}>
                            <div>
                                <label className="block text-sm font-medium mb-1">Display Name *</label>
                                <input
                                    type="text"
                                    placeholder="John Smith"
                                    required
                                    className="w-full p-3 rounded-lg border border-border bg-background"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Username *</label>
                                <input
                                    type="text"
                                    placeholder="jsmith"
                                    required
                                    className="w-full p-3 rounded-lg border border-border bg-background"
                                />
                                <p className="text-xs text-text-secondary mt-1">Used for login. Lowercase, no spaces.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Initial Password *</label>
                                <input
                                    type="password"
                                    placeholder="••••••••"
                                    required
                                    minLength={8}
                                    className="w-full p-3 rounded-lg border border-border bg-background"
                                />
                                <p className="text-xs text-text-secondary mt-1">Min 8 characters. User should change on first login.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Email (Optional)</label>
                                <input
                                    type="email"
                                    placeholder="jsmith@company.com"
                                    className="w-full p-3 rounded-lg border border-border bg-background"
                                />
                                <p className="text-xs text-text-secondary mt-1">For display purposes only. No email will be sent.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Role *</label>
                                <select className="w-full p-3 rounded-lg border border-border bg-background" required>
                                    <option value="engineer">Engineering User</option>
                                    <option value="projectManager">Project Manager</option>
                                    <option value="viewer">Read-Only Viewer</option>
                                    <option value="clientAdmin">Organization Administrator</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Initial Project Access</label>
                                <select className="w-full p-3 rounded-lg border border-border bg-background">
                                    <option value="">No projects (assign later)</option>
                                    <option value="*">All Projects</option>
                                    {MOCK_PROJECTS.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                                <strong>Note:</strong> Share the username and password with the new user directly.
                                They will be prompted to change their password on first login.
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="px-4 py-2 text-text-secondary hover:bg-background rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                                >
                                    Create User
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedUser && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card p-6 rounded-lg shadow-lg w-full max-w-md border border-border">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold flex items-center gap-2 text-red-600">
                                <Trash2 size={20} />
                                Delete User
                            </h3>
                            <button onClick={() => setShowDeleteModal(false)} className="text-text-secondary hover:text-text-primary">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <p className="text-text-primary">
                                Are you sure you want to delete <strong>{selectedUser.displayName}</strong>?
                            </p>
                            <p className="text-text-secondary text-sm">
                                Email: {selectedUser.email}
                            </p>
                            <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-sm text-red-700 dark:text-red-300">
                                <strong>Warning:</strong> This action cannot be undone. The user will lose all access to the platform.
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowDeleteModal(false)}
                                    className="px-4 py-2 text-text-secondary hover:bg-background rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteUser}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                >
                                    Delete User
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default ClientAdminDashboard;
